import { ScrollText, Sparkles, Square } from "lucide-react";
import { useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { MAX_CONVERSATION_MESSAGE_LENGTH } from "@/api/agent-conversation";
import { ConversationComposer } from "@/components/agents/conversation/ConversationComposer";
import { ConversationMessageList } from "@/components/agents/conversation/ConversationMessageList";
import { isConversationEnded } from "@/components/agents/conversation/conversationReducer";
import { QueueControlContext } from "@/components/agents/conversation/QueueControlContext";
import { activityText, conversationErrorText } from "@/components/agents/conversation/conversationTexts";
import {
  canType,
  pendingQuestionOf,
  type TerminalSource,
} from "@/components/agents/conversation/terminalSource";
import type { ConversationScreenState } from "@/components/agents/conversation/types";
import type { TerminalLogoMotion } from "@/components/agents/conversation/TerminalLogo";
import { usePrefetchScreenshots } from "@/components/agents/conversation/usePrefetchScreenshots";
import { MygrowIcon } from "@/components/brand/MygrowLogo";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import type { ConversationConnection } from "@/hooks/useAgentConversation";
import { FOCUS_RING_CLASS } from "@/lib/a11y";
import { cn } from "@/lib/utils";

// Botões do cabeçalho do console (Stop, End conversation), no estilo do Claude Design.
const HEADER_BUTTON = cn(
  "inline-flex h-8 items-center gap-1.5 rounded-md border border-[rgba(255,255,255,0.12)] bg-transparent px-3 text-[13px] font-medium text-white transition-colors [&_svg]:h-3 [&_svg]:w-3",
  "hover:bg-[rgba(255,255,255,0.06)] disabled:cursor-not-allowed disabled:text-[rgba(255,255,255,0.4)] disabled:hover:bg-transparent",
  FOCUS_RING_CLASS,
);

/** O que o agente faz agora, pelo estado da conversa e pelo último `agent_status`. */
function StatusIndicator({
  state,
  connection,
}: {
  state: ConversationScreenState;
  connection: ConversationConnection;
}) {
  const { t } = useTranslation();
  let label: string;
  // Leitura que falhou não é "carregando": o alerta com Retry aparece logo abaixo.
  if (state.status === null) label = connection === "error" ? t("Connection lost") : t("Loading conversation…");
  else if (isConversationEnded(state.status)) label = t("Conversation closed");
  else if (connection === "error") label = t("Connection lost");
  else if (state.status === "paused") label = t("Paused — send a message to resume");
  else
    label = activityText(
      t,
      state.activity ?? (state.status === "running" ? "thinking" : "waiting"),
    );
  const working = state.activity === "thinking" || state.activity === "acting";

  return (
    <span
      role="status"
      data-testid="conversation-status"
      className="flex min-w-0 items-center gap-2 text-[12px] text-[rgba(255,255,255,0.65)]"
    >
      <MygrowIcon
        title={null}
        className={cn("h-3.5 w-3.5 shrink-0 text-white", working && "motion-safe:animate-mygrow-nudge")}
      />
      <span className="truncate">{label}</span>
      {connection === "polling" && <span className="text-[#f2b84b]">· {t("Reconnecting…")}</span>}
    </span>
  );
}

/** A tag do cabeçalho enquanto o roteiro pronto roda sozinho, sem conversa. */
function ScriptRunTag() {
  const { t } = useTranslation();
  return (
    <span
      data-testid="script-run-tag"
      className="inline-flex w-fit items-center gap-1.5 rounded-full border border-[rgba(255,200,61,0.35)] bg-[rgba(255,200,61,0.12)] px-2 py-0.5 text-[11px] font-semibold text-[#ffc83d]"
    >
      <ScrollText aria-hidden="true" className="h-3 w-3" />
      {t("Running by script — no conversation")}
    </span>
  );
}

/** A moldura escura do console; o painel com e sem execução desenha a mesma. */
function TerminalFrame({ children, dialog }: { children: ReactNode; dialog?: ReactNode }) {
  return (
    <div
      data-testid="execution-terminal"
      className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#061e3d] text-[#d6dde8] shadow-[0_1px_2px_rgba(8,37,76,0.12)]"
    >
      <div data-testid="agent-conversation" className="flex min-h-0 flex-1 flex-col">
        {children}
      </div>
      {dialog}
    </div>
  );
}

/**
 * Cabeçalho do console (Claude Design): o ícone, "AGENT CONSOLE", o título da execução
 * ("código · nome" ou "No execution selected"), a linha de status e os botões.
 */
function ConsoleHeader({ title, status, actions }: { title: ReactNode; status?: ReactNode; actions: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="agent-console-header"
      className="flex items-center gap-3 border-b border-[rgba(255,255,255,0.08)] bg-[#08254c] px-4 py-3"
    >
      <span
        aria-hidden="true"
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[rgba(255,200,61,0.25)] bg-[rgba(255,200,61,0.1)] text-[#ffc83d]"
      >
        <Sparkles className="h-4 w-4" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-px">
        <span className="font-mono text-[10px] tracking-[0.12em] text-[rgba(255,255,255,0.5)]">
          {t("AGENT CONSOLE")}
        </span>
        {title}
        {status}
      </div>
      <span className="flex shrink-0 items-center gap-1.5">{actions}</span>
    </div>
  );
}

const TITLE_CLASS = "truncate text-sm font-semibold text-white";

/**
 * O terminal da etapa de execução: **um só**, igual para a estrutura organizacional,
 * SSCUI e App Fiori. Quem o usa entrega uma `TerminalSource`; ele não conhece o nó.
 *
 * Sem fonte (`source === null`) o painel continua de pé: mesma moldura e mesmo
 * cabeçalho ("Nenhuma execução selecionada", ou o `title` da linha escolhida), a linha
 * `placeholder` com o cursor no lugar do log e o campo fechado com a razão (`hint`, ou a
 * própria linha). O terminal nunca some da tela.
 *
 * Tema escuro contínuo (logs, falas, perguntas e avisos no mesmo fundo), prints em
 * miniatura, o logo do GROW no fim do log e um único campo de texto, que só abre quando
 * o agente espera a pessoa. Parar vale com turno ativo ou com o script rodando as
 * linhas (ou quando a fonte declara `canStop`) e não fecha o navegador; Encerrar pede
 * confirmação, porque sai da tela sem salvar o que não foi aprovado. As falhas dessas
 * ações são avisos fixos traduzidos: o `detail` da api é código e não vai cru para a
 * tela. Quem não é o executor (`source.watching`) só acompanha: sem campo, sem Parar e
 * sem Encerrar.
 *
 * Quem o monta o remonta (`key`) a cada troca de conversa: a identidade reinicia
 * cartões, rascunho e confirmação juntos.
 */
export function ExecutionTerminal({
  source,
  placeholder,
  hint,
  title,
}: {
  source: TerminalSource | null;
  /** A linha "> …" no lugar do log, quando não há fonte. */
  placeholder?: string;
  /** Razão do campo fechado sem fonte; sem ela vale a própria `placeholder`. */
  hint?: string;
  /** "código · nome" da linha escolhida; sem ele, "No execution selected". */
  title?: string | null;
}) {
  // Dois componentes e não um `if` no meio dos hooks: a fonte nula não chama nenhum.
  return source ? (
    <ConnectedTerminal source={source} title={title ?? null} />
  ) : (
    <EmptyTerminal placeholder={placeholder} hint={hint} title={title ?? null} />
  );
}

function EmptyTerminal({ placeholder, hint, title }: { placeholder?: string; hint?: string; title: string | null }) {
  const { t } = useTranslation();
  const reason = hint ?? placeholder ?? t("No execution selected");
  return (
    <TerminalFrame>
      <ConsoleHeader
        title={
          <span role="status" data-testid="conversation-status" className={TITLE_CLASS}>
            {title ?? t("No execution selected")}
          </span>
        }
        actions={
          <button type="button" className={HEADER_BUTTON} disabled>
            <Square aria-hidden="true" />
            {t("Stop")}
          </button>
        }
      />
      <ConversationMessageList
        projectId=""
        runId=""
        items={[]}
        pending={[]}
        disabled
        motion="still"
        emptyHint={placeholder}
        onRetry={() => {}}
      />
      <ConversationComposer
        disabled
        hint={reason}
        replying={false}
        maxLength={MAX_CONVERSATION_MESSAGE_LENGTH}
        onSend={() => {}}
      />
    </TerminalFrame>
  );
}

function ConnectedTerminal({ source, title }: { source: TerminalSource; title: string | null }) {
  const { t } = useTranslation();
  const { state, connection } = source;
  const watching = source.watching ?? null;
  const ended = isConversationEnded(state.status) || connection === "error";
  const pendingQuestion = pendingQuestionOf(state);
  const typing = canType(state, connection === "error") && (source.canReply ?? true);
  const [confirmingEnd, setConfirmingEnd] = useState(false);
  const queueControl = useContext(QueueControlContext);
  const scopeKey = source.scopeKey ?? source.media.runId;
  const scope = useMemo(() => ({ key: scopeKey, active: true }), [scopeKey]);
  const [stoppingScope, setStoppingScope] = useState<typeof scope | null>(null);
  const [endingScope, setEndingScope] = useState<typeof scope | null>(null);
  useEffect(() => {
    scope.active = true;
    return () => {
      scope.active = false;
    };
  }, [scope]);
  usePrefetchScreenshots(state.items, source.media);

  const stop = async () => {
    setStoppingScope(scope);
    try {
      await source.interrupt();
    } catch {
      if (scope.active) toast.error(t("Could not stop the agent."));
    } finally {
      if (scope.active) setStoppingScope(null);
    }
  };

  const end = async () => {
    if (!source.end) return;
    setEndingScope(scope);
    try {
      await source.end();
    } catch {
      if (scope.active) toast.error(t("Could not end the conversation."));
    } finally {
      if (scope.active) setEndingScope(null);
    }
  };

  const working =
    state.status === "running" && (state.activity === "thinking" || state.activity === "acting");
  // Na falha de conexão a dica some: o alerta do cabeçalho já explica (os controles
  // ficam travados como em conversa encerrada). Campo fechado pela fonte (`canReply`)
  // com o agente parado não diz "trabalhando": a fonte explica no log.
  const composerHint =
    connection === "error"
      ? null
      : ended
        ? t("The conversation is closed.")
        : state.status !== null && !typing && (source.canReply !== false || working)
          ? t("The agent is working… Use Stop to step in.")
          : null;
  const stoppable =
    source.canStop ?? (state.activeTurnId !== null || state.progress?.current != null);

  // O logo anima enquanto o agente trabalha, pula uma vez a cada item novo quando ele
  // espera, e fica parado com a conversa encerrada ou a conexão perdida.
  const logoMotion: TerminalLogoMotion = ended ? "still" : working ? "working" : "pulse";

  return (
    <TerminalFrame
      dialog={
        source.end && (
          <AlertDialog open={confirmingEnd && !ended} onOpenChange={setConfirmingEnd}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("End this conversation?")}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t(
                    "The agent leaves the screen without saving what was not approved and closes the browser. Rows not done stay pending.",
                  )}
                  {queueControl?.active && (
                    <>
                      {" "}
                      {t("Run all is active: stop only this unit and go to the next, or stop all of them?")}
                    </>
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("Cancel")}</AlertDialogCancel>
                {queueControl?.active ? (
                  <>
                    <AlertDialogAction onClick={() => void end()}>{t("Stop only this unit")}</AlertDialogAction>
                    <AlertDialogAction onClick={() => queueControl.stopAll()}>{t("Stop all")}</AlertDialogAction>
                  </>
                ) : (
                  <AlertDialogAction onClick={() => void end()}>{t("End conversation")}</AlertDialogAction>
                )}
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )
      }
    >
      <ConsoleHeader
        title={<span className={TITLE_CLASS}>{title ?? t("No execution selected")}</span>}
        status={
          <span className="flex min-w-0 flex-wrap items-center gap-x-2">
            <StatusIndicator state={state} connection={connection} />
            {state.scriptRun && <ScriptRunTag />}
            {state.progress && (
              <span data-testid="conversation-progress" className="font-mono text-[11px] text-[rgba(255,255,255,0.55)]">
                {t("{{done}} of {{total}} rows", { done: state.progress.done, total: state.progress.total })}
                {state.progress.failed > 0 &&
                  ` · ${t("Failed rows: {{failed}}", { failed: state.progress.failed })}`}
              </span>
            )}
          </span>
        }
        actions={
          watching ? null : (
            <>
              <button
                type="button"
                className={HEADER_BUTTON}
                disabled={
                  ended ||
                  state.status === "paused" ||
                  !stoppable ||
                  stoppingScope === scope
                }
                onClick={() => void stop()}
              >
                <Square aria-hidden="true" />
                {t("Stop")}
              </button>
              {source.end && (
                <button
                  type="button"
                  className={HEADER_BUTTON}
                  disabled={state.status === null || ended || endingScope === scope}
                  onClick={() => setConfirmingEnd(true)}
                >
                  {t("End conversation")}
                </button>
              )}
            </>
          )
        }
      />
      {source.connectionError && (
        <div role="alert" className="px-4 pt-2 text-[12px] text-[#ff9b9b]">
          {conversationErrorText(t, source.connectionError)}
          {source.retryConnection && (
            <Button type="button" variant="link" className="text-[#9cc0ff]" onClick={source.retryConnection}>
              {t("Retry")}
            </Button>
          )}
        </div>
      )}
      <ConversationMessageList
        projectId={source.media.projectId}
        runId={source.media.runId}
        items={state.items}
        pending={state.pending}
        disabled={ended || Boolean(watching)}
        watching={Boolean(watching)}
        motion={logoMotion}
        onRetry={source.retry}
      />
      {watching ? (
        <p
          data-testid="conversation-watching"
          className="m-0 border-t border-[rgba(255,255,255,0.08)] px-4 py-3.5 text-xs text-[rgba(255,255,255,0.55)]"
        >
          {watching.executorName
            ? t("{{name}}'s execution — you are watching", { name: watching.executorName })
            : t("Another user's execution — you are watching")}
        </p>
      ) : state.scriptRun ? (
        // O roteiro roda sozinho: o histórico segue visível, mas não há a quem falar.
        // O campo volta quando o roteiro desvia e o agente assume a conversa.
        <p
          data-testid="script-run-notice"
          className="m-0 border-t border-[rgba(255,255,255,0.08)] px-4 py-3.5 text-xs text-[rgba(255,255,255,0.55)]"
        >
          {t("The script is running. The chat opens if it needs you.")}
        </p>
      ) : (
        <ConversationComposer
          disabled={!typing}
          hint={composerHint}
          replying={pendingQuestion !== null}
          maxLength={source.maxMessageLength ?? MAX_CONVERSATION_MESSAGE_LENGTH}
          onSend={(text) => {
            const replyTo = pendingQuestion?.format === "free_text" ? pendingQuestion.questionId : null;
            void source.send(text, replyTo).catch(() => {});
          }}
        />
      )}
    </TerminalFrame>
  );
}
